import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Paper, Typography, IconButton, Stack } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PublicIcon from "@mui/icons-material/Public";
import HubIcon from "@mui/icons-material/Hub";
import StarsIcon from "@mui/icons-material/Stars";
import CelebrationIcon from "@mui/icons-material/Celebration";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import ForumIcon from "@mui/icons-material/Forum";
import GroupsIcon from "@mui/icons-material/Groups";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import PsychologyIcon from "@mui/icons-material/Psychology";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import DoNotTouchIcon from "@mui/icons-material/DoNotTouch";
import SocialDistanceIcon from "@mui/icons-material/SocialDistance";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LoopIcon from "@mui/icons-material/Loop";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import BoltIcon from "@mui/icons-material/Bolt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SpeedIcon from "@mui/icons-material/Speed";
import TimerIcon from "@mui/icons-material/Timer";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";

import { ResultCard, ResultCardType } from "../../shared/interfaces/GameLogicTypes";
import SocialGraphCard from "./SocialGraphCard";
import ShareTemplate from "./ShareTemplate";
import SvgDrawing from "../SvgDrawing/SvgDrawing";
import { track } from "../../shared/analytics";
import { stripCueMarkup } from "../../shared/helper/renderCueContent";

// ─── PNG font size tuning ─────────────────────────────────────────────────────
const PNG_FS_TITLE   = 64;   // card title
const PNG_FS_BODY    = 40;   // body / caption text (medium-length, with visual)
const PNG_FS_SMALL   = 34;   // ranking rows, inversion chips, predictor lines
const PNG_BAR_H      = 56;   // winner scoreboard bar height (px)
const PNG_ICON_SIZE  = 64;   // header icon
const PNG_GRAPH_SIZE = 750;  // social graph diameter
const PNG_NAME_COL_W = 280;  // fixed name-column width in bar chart (keeps bars aligned)
// ─────────────────────────────────────────────────────────────────────────────

// Card types that render a visual below the body text
const VISUAL_TYPES = new Set([
  ResultCardType.WINNER, ResultCardType.CARRY, ResultCardType.COMMUNITY_ALIGNMENT,
  ResultCardType.BEST_ROUND, ResultCardType.WORST_ROUND, ResultCardType.PERFECT_ROUND,
  ResultCardType.HARDEST_QUESTION, ResultCardType.FULL_CONSENSUS, ResultCardType.FULL_INVERSION, ResultCardType.SOCIAL_GRAPH,
  ResultCardType.BUCKLE,
  ResultCardType.CONFIDENTLY_INCORRECT,
  ResultCardType.ONE_STEP_AHEAD,
  ResultCardType.FTL_FIRST_BLOOD,
  ResultCardType.PICASSO_ROUND_GALLERY,
]);

// Scale body font by text length. Cards without a visual get a bigger base.
function pngBodyFontSize(text: string, hasVisual: boolean): number {
  const len = text.length;
  if (!hasVisual) {
    if (len < 30) return 90;
    if (len < 60) return 72;
    if (len < 100) return 56;
    return 44;
  }
  if (len < 30) return 56;
  if (len < 60) return PNG_FS_BODY;
  if (len < 100) return 30;
  return 24;
}

// Game card slot colours (matching the 5 cue slots in-game)
const CUE_BG = ["#fff355", "#608ad6", "#e75151", "#92dc67", "#e17d47"];
const CUE_FG = ["#333333", "#ffffff", "#ffffff", "#ffffff", "#ffffff"];

interface ResultCardPanelProps {
  card: ResultCard;
  playerNames?: string[];
  /** Grow the winner's scoreboard bars from empty — used by the end-of-game centre reveal. */
  animateBars?: boolean;
}

// Highlight all player names in primary colour
function HighlightedText({ text, names }: { text: string; names: string[] }) {
  if (!names.length || !text) return <>{text}</>;
  const escaped = names
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .sort((a, b) => b.length - a.length)
    .join("|");
  const parts = text.split(new RegExp(`(${escaped})`, "g"));
  const nameSet = new Set(names);
  return (
    <>
      {parts.map((part, i) =>
        nameSet.has(part) ? (
          <Box key={i} component="span" sx={{ color: "primary.main", fontWeight: "bold" }}>
            {part}
          </Box>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

function getIcon(type: ResultCardType, png = false) {
  const sx = { fontSize: png ? PNG_ICON_SIZE : { xs: 38, md: 28 } };
  switch (type) {
    case ResultCardType.WINNER: return <EmojiEventsIcon sx={{ ...sx, color: "#ffd700" }} />;
    case ResultCardType.COMMUNITY_ALIGNMENT: return <PublicIcon sx={{ ...sx, color: "#4fc3f7" }} />;
    case ResultCardType.SOCIAL_GRAPH: return <HubIcon sx={sx} />;
    case ResultCardType.PERFECT_ROUND: return <StarsIcon sx={{ ...sx, color: "#ffd700" }} />;
    case ResultCardType.BEST_ROUND: return <CelebrationIcon sx={{ ...sx, color: "#4caf50" }} />;
    case ResultCardType.WORST_ROUND: return <SentimentVeryDissatisfiedIcon sx={{ ...sx, color: "#f44336" }} />;
    case ResultCardType.HARDEST_QUESTION: return <QuestionMarkIcon sx={sx} />;
    case ResultCardType.FULL_CONSENSUS: return <GroupsIcon sx={sx} />;
    case ResultCardType.FULL_INVERSION: return <SwapVertIcon sx={sx} />;
    case ResultCardType.MIND_READER: return <PsychologyIcon sx={{ ...sx, color: "#9c27b0" }} />;
    case ResultCardType.PICKY: return <DoNotDisturbIcon sx={sx} />;
    case ResultCardType.WE_DONT_TALK_ABOUT_IT: return <DoNotTouchIcon sx={sx} />;
    case ResultCardType.COGNITIVE_DISSONANCE: return <SocialDistanceIcon sx={sx} />;
    case ResultCardType.SIMP: return <FavoriteIcon sx={{ ...sx, color: "#e91e63" }} />;
    case ResultCardType.DEADWEIGHT: return <AutoAwesomeIcon sx={{ ...sx, color: "#9c27b0" }} />;
    case ResultCardType.CARRY: return <BoltIcon sx={{ ...sx, color: "#ff9800" }} />;
    case ResultCardType.CIRCULAR_CHAIN: return <LoopIcon sx={{ ...sx, color: "#e91e63" }} />;
    case ResultCardType.COMEBACK: return <TrendingUpIcon sx={{ ...sx, color: "#4caf50" }} />;
    case ResultCardType.CONSISTENT: return <HorizontalRuleIcon sx={sx} />;
    case ResultCardType.SPEED_RUNNER: return <SpeedIcon sx={{ ...sx, color: "#00bcd4" }} />;
    case ResultCardType.QUICK_TO_JUDGE: return <TimerIcon sx={{ ...sx, color: "#ff9800" }} />;
    case ResultCardType.CONFIDENTLY_INCORRECT: return <ThumbDownIcon sx={{ ...sx, color: "#f44336" }} />;
    case ResultCardType.ONE_STEP_AHEAD: return <FlashOnIcon sx={{ ...sx, color: "#ffd700" }} />;
    case ResultCardType.WEAKEST_LINK: return <HourglassBottomIcon sx={{ ...sx, color: "#9e9e9e" }} />;
    case ResultCardType.BUCKLE: return <PsychologyAltIcon sx={{ ...sx, color: "#9c27b0" }} />;
    case ResultCardType.WE_NEED_TO_TALK: return <ForumIcon sx={{ ...sx, color: "#90caf9" }} />;
    case ResultCardType.FTL_FIRST_BLOOD: return <GpsFixedIcon sx={{ ...sx, color: "#f44336" }} />;
    default: return null;
  }
}

// Replace role tokens and remove inline cue markup before result-card rendering.
function formatQuestion(q: string | undefined, tsarName: string): string {
  if (!q) return "";
  return stripCueMarkup(q).replace(/\{\{tsar\}\}/g, tsarName);
}

// ─── Shared: Tsar ranking visual ─────────────────────────────────────────────
function TsarRankingVisual({ data, footerLabel, png = false }: { data: Record<string, unknown>; footerLabel?: string; png?: boolean }) {
  const tsarSequence = data.tsarSequence as number[] | undefined;
  const cues = data.cues as string[] | undefined;
  const avgScore = data.avgScore as number | undefined;
  const fs = png ? PNG_FS_SMALL : { xs: 12, md: 9 };

  if (!tsarSequence || !cues || tsarSequence.length === 0) return null;

  return (
    <Stack spacing={png ? 2 : 0.3} sx={{ width: "100%", mt: 0.5 }}>
      {tsarSequence.map((cueNum, rank) => {
        const cueIdx = cueNum - 1;
        const content = stripCueMarkup((cues[cueIdx] as string) ?? "?");
        const bg = CUE_BG[cueIdx] ?? "#aaa";
        const fg = CUE_FG[cueIdx] ?? "#fff";
        return (
          <Box key={rank} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ minWidth: 14, textAlign: "right", fontSize: fs, color: "text.secondary", flexShrink: 0 }}>
              {rank + 1}.
            </Typography>
            <Box sx={{ flex: 1, bgcolor: bg, color: fg, borderRadius: 0.5, px: 0.75, py: 0.4, fontSize: fs, fontWeight: 600, lineHeight: 1.4, wordBreak: "break-word" }}>
              {content}
            </Box>
          </Box>
        );
      })}
      {avgScore !== undefined && (
        <Typography color="text.secondary" sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 9 }, mt: 0.25 }}>
          {footerLabel ?? "Avg group score"}: {avgScore} pts
        </Typography>
      )}
    </Stack>
  );
}

// ─── Hardest question: ranking + best/worst predictor ────────────────────────
function HardestQuestionVisual({ data, png = false }: { data: Record<string, unknown>; png?: boolean }) {
  const bestPredictor = data.bestPredictor as { name: string; pts: number } | undefined;
  const worstPredictor = data.worstPredictor as { name: string; pts: number } | undefined;
  const fs = png ? PNG_FS_SMALL : { xs: 12, md: 9 };
  return (
    <Stack spacing={0.25} sx={{ width: "100%", mt: 0.5 }}>
      <TsarRankingVisual data={data} footerLabel="Avg score" png={png} />
      {(bestPredictor || worstPredictor) && (
        <Box sx={{ p: 0.5, bgcolor: "rgba(255,255,255,0.05)", borderRadius: 1, mt: 0.25 }}>
          {bestPredictor && (
            <Typography sx={{ fontSize: png ? PNG_FS_BODY : fs, color: "success.main", display: "block" }}>
              Best: {bestPredictor.name} ({bestPredictor.pts} pts)
            </Typography>
          )}
          {worstPredictor && (
            <Typography sx={{ fontSize: png ? PNG_FS_BODY : fs, color: "error.main", display: "block" }}>
              Worst: {worstPredictor.name} ({worstPredictor.pts} pts)
            </Typography>
          )}
        </Box>
      )}
      <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "text.secondary", mt: 0.25 }}>
        This split the group.
      </Typography>
    </Stack>
  );
}

// ─── We need to talk: tsar ranking + concluding line ───────────────────────
function WeNeedToTalkVisual({ data, png = false }: { data: Record<string, unknown>; png?: boolean }) {
  return (
    <Stack spacing={0.5} sx={{ width: "100%", mt: 0.5 }}>
      <TsarRankingVisual data={data} png={png} />
      <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "text.secondary" }}>
        this had to be discussed
      </Typography>
    </Stack>
  );
}

// ─── Single player sequence (used by Buckle) ────────────────────────────────
function PlayerSequenceVisual({ data, png = false }: { data: Record<string, unknown>; png?: boolean }) {
  const cues = data.cues as string[] | undefined;
  const sequence = data.sequence as number[] | undefined;
  const playerName = data.playerName as string | undefined;
  const secondsTaken = data.secondsTaken as number | undefined;
  const personalAvgSecs = data.personalAvgSecs as number | undefined;
  if (!cues || !sequence || sequence.length === 0) return null;
  const fs = png ? PNG_FS_SMALL : { xs: 11, md: 8 };
  return (
    <Stack spacing={0.5} sx={{ mt: 1, width: "100%" }}>
      {playerName && (
        <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: png ? PNG_FS_BODY : { xs: 13, md: 10 } }}>
          {playerName}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        {sequence.map((cueNum, i) => {
          const cueIdx = cueNum - 1;
          return (
            <Box key={i} sx={{ bgcolor: CUE_BG[cueIdx] ?? "#aaa", color: CUE_FG[cueIdx] ?? "#fff", borderRadius: 0.5, px: 0.5, fontSize: fs, fontWeight: 600, lineHeight: 1.6 }}>
              {stripCueMarkup(cues[cueIdx] ?? "?").slice(0, 14)}
            </Box>
          );
        })}
      </Box>
      {(secondsTaken !== undefined || personalAvgSecs !== undefined) && (
        <Typography color="text.secondary" sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 9 } }}>
          {secondsTaken !== undefined && personalAvgSecs !== undefined
            ? `${secondsTaken}s (avg ${personalAvgSecs}s)`
            : secondsTaken !== undefined
            ? `${secondsTaken}s`
            : `avg ${personalAvgSecs}s`}
        </Typography>
      )}
    </Stack>
  );
}

// ─── Confidently Incorrect: question + predicted sequence ──────────────────
function ConfidentWrongVisual({ data, png = false }: { data: Record<string, unknown>; png?: boolean }) {
  const tsarName = data.tsarName as string | undefined;
  const playerName = data.playerName as string | undefined;
  const cues = data.cues as string[] | undefined;
  const seq = data.sequence as number[] | undefined;
  const score = data.score as number | undefined;
  const secondsTaken = data.secondsTaken as number | undefined;
  if (!tsarName || !playerName || !cues || !seq || seq.length === 0) return null;
  return (
    <Stack spacing={0.75} sx={{ mt: 1, width: "100%" }}>
      <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "text.secondary" }}>
        {playerName} predicted for {tsarName}:
      </Typography>
      <TsarRankingVisual data={{ tsarSequence: seq, cues }} png={png} />
      {(score !== undefined || secondsTaken !== undefined) && (
        <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "text.secondary" }}>
          {secondsTaken !== undefined && `answered in ${secondsTaken}s`}
          {secondsTaken !== undefined && score !== undefined ? " - " : ""}
          {score !== undefined && `only scored ${score} pts`}
        </Typography>
      )}
      <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "error.main" }}>
        confident swing - glorious whiff.
      </Typography>
    </Stack>
  );
}

// ─── Winner / Carry scoreboard ───────────────────────────────────────────────
function WinnerVisual({ data, png = false, grow = false }: { data: Record<string, unknown>; png?: boolean; grow?: boolean }) {
  const allScores = data.allScores as { name: string; score: number }[] | undefined;
  const winnerNames = data.winnerNames as string[] | undefined;
  // Bars start empty and animate out to their real width on the next frame — the growth is the
  // reveal, so it has to happen after mount rather than being the initial paint.
  const [grown, setGrown] = useState(!grow);
  useEffect(() => {
    if (!grow) return;
    const id = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(id);
  }, [grow]);
  if (!allScores || allScores.length === 0) return null;
  const maxScore = Math.max(...allScores.map((s) => s.score), 1);
  const winnerSet = new Set(winnerNames ?? [allScores[0].name]);
  const fs = png ? PNG_FS_SMALL : { xs: "0.8rem", md: "0.7rem" };
  return (
    <Stack spacing={0.5} sx={{ width: "100%", mt: 1 }}>
      {allScores.map((s, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ width: png ? PNG_NAME_COL_W : { xs: 100, md: 80 }, flexShrink: 0, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", fontSize: fs }}>
            {s.name}
          </Typography>
          <Box sx={{ flex: 1, bgcolor: "rgba(255,255,255,0.1)", borderRadius: 1, height: png ? PNG_BAR_H : { xs: 14, md: 10 }, overflow: "hidden" }}>
            <Box
              sx={{
                width: grown ? `${(s.score / maxScore) * 100}%` : "0%",
                bgcolor: winnerSet.has(s.name) ? "primary.main" : "secondary.main",
                height: "100%",
                borderRadius: 1,
                transition: grow ? `width 1100ms cubic-bezier(.2,.8,.3,1) ${i * 120}ms` : undefined,
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ fontSize: fs }}>{s.score}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

// ─── Community alignment: agreement with the wider playerbase ────────────────
function CommunityAlignmentVisual({ data, png = false }: { data: Record<string, unknown>; png?: boolean }) {
  const players = data.players as { name: string; alignment: number }[] | undefined;
  const groupAlignment = data.groupAlignment as number | undefined;
  if (!players || players.length === 0 || groupAlignment === undefined) return null;
  const fs = png ? PNG_FS_SMALL : { xs: "0.8rem", md: "0.7rem" };

  // Bar length matches the number exactly. An earlier version amplified the bar around the 50%
  // random-chance midpoint for contrast, which made a 60% player show a 20% bar — the spread
  // between real scores is wide enough to read without that, and a bar that disagrees with its
  // own label is worse than a short one.
  const barWidth = (pct: number) => `${Math.max(0, Math.min(100, pct))}%`;
  const rows = [
    { name: "The group", alignment: groupAlignment, isGroup: true },
    ...players.map((p) => ({ ...p, isGroup: false })),
  ];

  return (
    <Stack spacing={0.5} sx={{ width: "100%", mt: 1 }}>
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{
              width: png ? PNG_NAME_COL_W : { xs: 100, md: 80 },
              flexShrink: 0,
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              fontSize: fs,
              fontWeight: r.isGroup ? 700 : 400,
            }}
          >
            {r.name}
          </Typography>
          <Box sx={{ flex: 1, bgcolor: "rgba(255,255,255,0.1)", borderRadius: 1, height: png ? PNG_BAR_H : { xs: 14, md: 10 }, overflow: "hidden" }}>
            <Box sx={{ width: barWidth(r.alignment), bgcolor: r.isGroup ? "primary.main" : "secondary.main", height: "100%", borderRadius: 1 }} />
          </Box>
          <Typography variant="caption" sx={{ fontSize: fs, fontWeight: r.isGroup ? 700 : 400 }}>
            {r.alignment}%
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

// ─── Inversion: side-by-side sequences ──────────────────────────────────────
function InversionVisual({ data, png = false }: { data: Record<string, unknown>; png?: boolean }) {
  const cues = data.cues as string[] | undefined;
  const pA = data.playerA as { name: string; sequence: number[] } | undefined;
  const pB = data.playerB as { name: string; sequence: number[] } | undefined;
  const question = data.question as string | undefined;
  if (!cues || !pA || !pB || !question) return null;
  const fs = png ? PNG_FS_SMALL : { xs: 11, md: 8 };

  // Funny banter lines for getting it completely backwards
  const banterLines = [
    "nailed it... in reverse.",
    "read that room upside down.",
    "absolute masterclass in wrongness.",
    "really committed to that take.",
    "polar opposite energy.",
    "decided to go full contrarian.",
  ];
  const banter = banterLines[Math.floor(Math.random() * banterLines.length)];

  return (
    <Stack spacing={0.75} sx={{ mt: 1, width: "100%" }}>
      {/* Show the question */}
      <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "text.secondary", fontStyle: "italic" }}>
        The question: {stripCueMarkup(question)}
      </Typography>

      {/* Show only playerB (the inverted/wrong one) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        <Typography variant="caption" sx={{ minWidth: "auto", fontWeight: "bold", fontSize: fs }}>{pB.name}:</Typography>
        {pB.sequence.map((cueNum, i) => {
          const cueIdx = cueNum - 1;
          return (
            <Box key={i} sx={{ bgcolor: CUE_BG[cueIdx] ?? "#aaa", color: CUE_FG[cueIdx] ?? "#fff", borderRadius: 0.5, px: 0.5, fontSize: fs, fontWeight: 600, lineHeight: 1.6 }}>
              {stripCueMarkup(cues[cueIdx] ?? "?").slice(0, 10)}
            </Box>
          );
        })}
      </Box>

      {/* Funny banter */}
      <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "text.secondary", fontStyle: "italic" }}>
        {banter}
      </Typography>
    </Stack>
  );
}

// ─── Picasso Gallery: animated drawing slideshow ────────────────────────────
const GALLERY_NAME_COLORS = ["#e75151", "#608ad6", "#92dc67", "#e17d47", "#c06ee7", "#47c0b0", "#f5a623"];

function PicassoGalleryVisual({ data, png = false }: { data: Record<string, unknown>; png?: boolean }) {
  const drawings = data.drawings as { playerName: string; drawingSvg: string }[] | undefined;
  const question = data.question as string | undefined;
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!drawings || drawings.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % drawings.length);
    }, 1500);
    return () => clearInterval(timer);
  }, [drawings]);

  if (!drawings || drawings.length === 0) return null;

  const drawing = drawings[activeIdx];
  const nameColor = GALLERY_NAME_COLORS[activeIdx % GALLERY_NAME_COLORS.length];
  return (
    <Stack spacing={0} sx={{ flex: 1, minHeight: 0, width: "100%", alignItems: "center", overflow: "hidden" }}>
      <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, fontWeight: "bold", color: nameColor, textAlign: "center", lineHeight: 1.2, flexShrink: 0 }}>
        {drawing.playerName}
      </Typography>
      <Box sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        ...(png && { width: 400, height: 400 }),
      }}>
        {drawings.map((d, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: i === activeIdx ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
              "& > div": { maxWidth: "100%", maxHeight: "100%" },
            }}
          >
            <SvgDrawing svg={d.drawingSvg} width="100%" height="100%" />
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: png ? PNG_FS_SMALL : { xs: 9, md: 7 }, color: "text.secondary", lineHeight: 1, flexShrink: 0 }}>
        {activeIdx + 1} / {drawings.length}
      </Typography>
    </Stack>
  );
}

// ─── GIF generation helpers ─────────────────────────────────────────────────

const BRAND_PRIMARY = "#ec7777";
const DOT_COLORS = ["#e75151", "#608ad6", "#92dc67", "#fff355", "#e17d47"];

function drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function fillWarmBg(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const grad = ctx.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, "#e8e0d4");
  grad.addColorStop(0.4, "#ddd6cc");
  grad.addColorStop(1, "#e0c9c2");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
}

function drawDots(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number) {
  const dotR = radius * 0.18;
  const angles = [-Math.PI / 2, -Math.PI / 2 + (2 * Math.PI) / 5, -Math.PI / 2 + (4 * Math.PI) / 5,
                   -Math.PI / 2 + (6 * Math.PI) / 5, -Math.PI / 2 + (8 * Math.PI) / 5];
  angles.forEach((a, i) => {
    ctx.beginPath();
    ctx.arc(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius, dotR, 0, Math.PI * 2);
    ctx.fillStyle = DOT_COLORS[i];
    ctx.fill();
  });
}

function drawBrandFooter(ctx: CanvasRenderingContext2D, w: number, h: number) {
  // Small dots
  drawDots(ctx, w / 2, h - 58, 16);
  // "rank5.io" text
  ctx.fillStyle = BRAND_PRIMARY;
  ctx.font = "bold 28px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("rank5.io", w / 2, h - 36);
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const test = line ? line + " " + word : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawGifQuestionFrame(ctx: CanvasRenderingContext2D, w: number, h: number, question: string) {
  fillWarmBg(ctx, w, h);

  // Decorative dots above question
  drawDots(ctx, w / 2, h / 2 - 160, 40);

  // "Picasso Gallery" header
  ctx.fillStyle = "#555555";
  ctx.font = "600 24px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("PICASSO GALLERY", w / 2, h / 2 - 80);

  // Decorative line
  ctx.strokeStyle = BRAND_PRIMARY;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(w / 2 - 80, h / 2 - 55);
  ctx.lineTo(w / 2 + 80, h / 2 - 55);
  ctx.stroke();

  // Question text — white card
  const cardW = w - 80;
  const lines = (() => {
    ctx.font = "italic 32px sans-serif";
    return wrapText(ctx, `"${question}"`, cardW - 60);
  })();
  const lineH = 44;
  const cardH = Math.max(120, lines.length * lineH + 60);
  const cardX = (w - cardW) / 2;
  const cardY = h / 2 - 20;

  ctx.fillStyle = "rgba(255,255,255,0.85)";
  drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 16);
  ctx.fill();

  ctx.fillStyle = "#444444";
  ctx.font = "italic 32px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const textStartY = cardY + cardH / 2 - ((lines.length - 1) * lineH) / 2;
  lines.forEach((line, i) => {
    ctx.fillText(line, w / 2, textStartY + i * lineH);
  });

  drawBrandFooter(ctx, w, h);
}

function drawGifCtaFrame(ctx: CanvasRenderingContext2D, w: number, h: number) {
  fillWarmBg(ctx, w, h);

  // Big dots
  drawDots(ctx, w / 2, h / 2 - 100, 60);

  // "rank5.io" big
  ctx.fillStyle = BRAND_PRIMARY;
  ctx.font = "bold 64px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("rank5.io", w / 2, h / 2 + 20);

  // Tagline
  ctx.fillStyle = "#666666";
  ctx.font = "28px sans-serif";
  ctx.fillText("Find out what your friends", w / 2, h / 2 + 90);
  ctx.fillText("think of you", w / 2, h / 2 + 126);

  // Subtle bottom line
  ctx.strokeStyle = "rgba(0,0,0,0.1)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(w / 2 - 120, h / 2 + 170);
  ctx.lineTo(w / 2 + 120, h / 2 + 170);
  ctx.stroke();

  ctx.fillStyle = "#999999";
  ctx.font = "22px sans-serif";
  ctx.fillText("Play at rank5.io", w / 2, h / 2 + 200);
}

async function svgToImage(svgStr: string, width: number, height: number): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgStr);
  img.width = width;
  img.height = height;
  await img.decode();
  return img;
}

async function drawGifDrawingFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  svgStr: string,
  playerName: string,
  color: string,
) {
  fillWarmBg(ctx, w, h);

  // Drawing card — centered vertically
  const drawSize = 420;
  const cardPad = 24;
  const nameH = 56;
  const totalCardH = nameH + drawSize + cardPad * 2;
  const cardX = (w - drawSize - cardPad * 2) / 2;
  const cardY = (h - totalCardH) / 2;

  // Card shadow
  ctx.fillStyle = "rgba(0,0,0,0.08)";
  drawRoundedRect(ctx, cardX + 4, cardY + 6, drawSize + cardPad * 2, totalCardH, 20);
  ctx.fill();

  // Card background
  ctx.fillStyle = "#ffffff";
  drawRoundedRect(ctx, cardX, cardY, drawSize + cardPad * 2, totalCardH, 20);
  ctx.fill();

  // Player name inside card top
  ctx.fillStyle = color;
  ctx.font = "bold 36px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(playerName, w / 2, cardY + nameH / 2 + cardPad / 2);

  // Thin separator
  ctx.strokeStyle = "rgba(0,0,0,0.08)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cardX + 20, cardY + nameH + cardPad / 2);
  ctx.lineTo(cardX + drawSize + cardPad * 2 - 20, cardY + nameH + cardPad / 2);
  ctx.stroke();

  // Drawing area
  const drawX = cardX + cardPad;
  const drawY = cardY + nameH + cardPad;

  try {
    const img = await svgToImage(svgStr, drawSize, drawSize);
    ctx.drawImage(img, drawX, drawY, drawSize, drawSize);
  } catch {
    // SVG render failed — white area remains
  }

  drawBrandFooter(ctx, w, h);
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ResultCardPanel({ card, playerNames = [], animateBars = false }: ResultCardPanelProps) {
  const shareRef = useRef<HTMLDivElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  const isGifCard = card.type === ResultCardType.PICASSO_ROUND_GALLERY;
  const fileExt = isGifCard ? "gif" : "png";
  const mimeType = isGifCard ? "image/gif" : "image/png";

  async function handleGifShare() {
    track("result_card_render_gif", { cardType: card.type, cardTitle: card.title });
    setRendering(true);
    setDialogOpen(true);

    try {
      const GIF = (await import("gif.js")).default;
      const gif = new GIF({
        workers: 2,
        quality: 10,
        width: 540,
        height: 960,
        workerScript: "/gif.worker.js",
      });

      const canvas = document.createElement("canvas");
      canvas.width = 540;
      canvas.height = 960;
      const ctx = canvas.getContext("2d")!;

      const drawings = card.data.drawings as { playerName: string; drawingSvg: string }[];
      const question = stripCueMarkup((card.data.question as string) || card.body);

      // Frame 1: Question
      drawGifQuestionFrame(ctx, 540, 960, question);
      gif.addFrame(ctx, { delay: 2000, copy: true });

      // Frames 2-N: Each drawing
      for (let i = 0; i < drawings.length; i++) {
        const d = drawings[i];
        const color = GALLERY_NAME_COLORS[i % GALLERY_NAME_COLORS.length];
        await drawGifDrawingFrame(ctx, 540, 960, d.drawingSvg, d.playerName, color);
        gif.addFrame(ctx, { delay: 1500, copy: true });
      }

      // Final frame: CTA
      drawGifCtaFrame(ctx, 540, 960);
      gif.addFrame(ctx, { delay: 2500, copy: true });

      gif.on("finished", (blob: Blob) => {
        setPreviewUrl(URL.createObjectURL(blob));
        setRendering(false);
      });
      gif.render();
    } catch (err) {
      console.error("GIF render failed", err);
      setRendering(false);
    }
  }

  async function handleShareClick() {
    if (isGifCard) return handleGifShare();
    if (!shareRef.current) return;
    track("result_card_render_png", { cardType: card.type, cardTitle: card.title });
    setRendering(true);
    setDialogOpen(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(shareRef.current, { useCORS: true, scale: 2 });
      setPreviewUrl(canvas.toDataURL("image/png"));
    } catch (err) {
      console.error("render failed", err);
    } finally {
      setRendering(false);
    }
  }

  function handleClose() {
    setDialogOpen(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }

  async function handleDownload() {
    if (!previewUrl) return;
    track("result_card_download", { cardType: card.type, cardTitle: card.title });
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `${card.title.replace(/\s+/g, "_")}.${fileExt}`;
    a.click();
  }

  async function handleShare() {
    if (!previewUrl) return;
    track("result_card_share", { cardType: card.type, cardTitle: card.title });
    try {
      const res = await fetch(previewUrl);
      const blob = await res.blob();
      const file = new File([blob], `${card.title.replace(/\s+/g, "_")}.${fileExt}`, { type: mimeType });
      await navigator.share({ files: [file], title: card.title });
    } catch (err) {
      console.error("share failed", err);
    }
  }

  const renderVisual = (png: boolean) => {
    switch (card.type) {
      case ResultCardType.WINNER:
      case ResultCardType.CARRY:
        return <WinnerVisual data={card.data} png={png} grow={animateBars && !png} />;
      case ResultCardType.COMMUNITY_ALIGNMENT:
        return <CommunityAlignmentVisual data={card.data} png={png} />;
      case ResultCardType.BEST_ROUND:
      case ResultCardType.WORST_ROUND:
      case ResultCardType.PERFECT_ROUND:
        return <TsarRankingVisual data={card.data} footerLabel="Avg group score" png={png} />;
      case ResultCardType.HARDEST_QUESTION:
        return <HardestQuestionVisual data={card.data} png={png} />;
      case ResultCardType.FULL_CONSENSUS:
        return <PlayerSequenceVisual data={card.data} png={png} />;
      case ResultCardType.FULL_INVERSION:
        return <InversionVisual data={card.data} png={png} />;
      case ResultCardType.BUCKLE: {
        // Show the player's submission as a vertical ranking, like Perfect Round
        const cues = card.data.cues as string[] | undefined;
        const seq = card.data.sequence as number[] | undefined;
        const playerName = (card.data.playerName as string) ?? "Someone";
        const secondsTaken = card.data.secondsTaken as number | undefined;
        const personalAvgSecs = card.data.personalAvgSecs as number | undefined;
        return (
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {cues && seq && seq.length > 0 && (
              <TsarRankingVisual data={{ tsarSequence: seq, cues }} png={png} />
            )}
            {(secondsTaken !== undefined || personalAvgSecs !== undefined) && (
              <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "text.secondary" }}>
                It took {playerName} {secondsTaken ?? "?"}s to answer this,
                {" "}
                {personalAvgSecs !== undefined ? `despite usually answering in ${personalAvgSecs}s.` : `which was slower than usual.`}
              </Typography>
            )}
          </Stack>
        );
      }
      case ResultCardType.CONFIDENTLY_INCORRECT:
        return <ConfidentWrongVisual data={card.data} png={png} />;
      case ResultCardType.WE_NEED_TO_TALK:
        return <WeNeedToTalkVisual data={card.data} png={png} />;
      case ResultCardType.ONE_STEP_AHEAD: {
        const cues = card.data.cues as string[] | undefined;
        const seq = card.data.sequence as number[] | undefined;
        const czarName = (card.data.czarName as string) ?? "?";
        const playerName = (card.data.playerName as string) ?? "Someone";
        return (
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "text.secondary" }}>
              {playerName} predicted for {czarName}:
            </Typography>
            {cues && seq && seq.length > 0 && (
              <TsarRankingVisual data={{ tsarSequence: seq, cues }} png={png} />
            )}
            <Typography sx={{ fontSize: png ? PNG_FS_BODY : { xs: 12, md: 10 }, color: "primary.main" }}>
              locked it in {(card.data.gapSecs as number)?.toFixed ? (card.data.gapSecs as number).toFixed(1) : (card.data.gapSecs as number)}s before {czarName} — and delivered a perfect prediction
            </Typography>
          </Stack>
        );
      }
      case ResultCardType.FTL_FIRST_BLOOD: {
        const playerName = (card.data.playerName as string) ?? "Someone";
        const correctAnswer = stripCueMarkup((card.data.correctAnswer as string) ?? "?");
        const playerAnswer = stripCueMarkup((card.data.playerAnswer as string) ?? "?");
        const fs = png ? PNG_FS_BODY : { xs: 12, md: 10 };
        return (
          <Stack spacing={1} sx={{ mt: 1, width: "100%" }}>
            <Stack spacing={0.5}>
              <Typography sx={{ fontSize: fs, color: "success.main" }}>
                Correct: {correctAnswer}
              </Typography>
              <Typography sx={{ fontSize: fs, color: "error.main" }}>
                {playerName} picked: {playerAnswer}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: fs, color: "text.secondary", fontStyle: "italic" }}>
              eliminated on the very first turn.
            </Typography>
          </Stack>
        );
      }
      case ResultCardType.SOCIAL_GRAPH: {
        const nodes = card.data.nodes as { id: string; name: string }[] | undefined;
        const edges = card.data.edges as { from: string; to: string; accuracy: number }[] | undefined;
        if (!nodes || !edges) return null;
        return <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}><SocialGraphCard nodes={nodes} edges={edges} size={png ? PNG_GRAPH_SIZE : undefined} /></Box>;
      }
      case ResultCardType.PICASSO_ROUND_GALLERY:
        return <PicassoGalleryVisual data={card.data} png={png} />;
      default:
        return null;
    }
  };

  // For cards that have a tsar ranking, the body IS the formatted question
  const bodyIsQuestion = [
    ResultCardType.BEST_ROUND,
    ResultCardType.WORST_ROUND,
    ResultCardType.PERFECT_ROUND,
    ResultCardType.HARDEST_QUESTION,
    ResultCardType.FULL_CONSENSUS,
    ResultCardType.BUCKLE,
    ResultCardType.ONE_STEP_AHEAD,
    ResultCardType.FTL_FIRST_BLOOD,
    ResultCardType.PICASSO_ROUND_GALLERY,
  ].includes(card.type);

  const hasVisual = VISUAL_TYPES.has(card.type);
  const formattedBody = formatQuestion(
    card.body,
    (card.data.tsarName as string) ?? ""
  );

  const buildCardContent = (png: boolean) => (
    <Stack
      sx={{
        width: "100%",
        height: png ? "auto" : "100%",
        p: png ? 5 : 1.5,
        // Keep clear of the absolutely-positioned share button — with a content-sized card there
        // is no leftover space to absorb it.
        pb: png ? 5 : 4.5,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
      spacing={0.5}
    >
      {/* Header row */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={png ? 2 : 1}
        sx={png ? { pb: 2, mb: 1, borderBottom: "3px solid", borderColor: "primary.main" } : undefined}
      >
        {getIcon(card.type, png)}
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            flex: 1,
            lineHeight: 1.2,
            fontSize: png ? PNG_FS_TITLE : { xs: "1.05rem", md: "1rem" },
            ...(png && { letterSpacing: "0.01em" }),
          }}
        >
          {card.title}
        </Typography>
      </Stack>

      {/* Body */}
      <Box>
      {bodyIsQuestion ? (
        <Typography sx={{ fontStyle: "italic", lineHeight: 1.3, fontSize: png ? pngBodyFontSize(formattedBody, hasVisual) : { xs: "0.85rem", md: "0.75rem" } }} variant="caption">
          <HighlightedText
            text={formattedBody}
            names={playerNames}
          />
        </Typography>
      ) : (
        <Typography sx={{ lineHeight: 1.4, fontSize: png ? pngBodyFontSize(formattedBody, hasVisual) : { xs: "0.95rem", md: "0.875rem" } }} variant="body2">
          <HighlightedText text={formattedBody} names={playerNames} />
        </Typography>
      )}
      </Box>

      {/* Type-specific visual */}
      <Box sx={{ flex: png ? "unset" : 1, minHeight: 0, overflowY: png ? "hidden" : (isGifCard ? "hidden" : "auto"), overflowX: "hidden", display: "flex", flexDirection: "column" }}>
        {renderVisual(png)}
      </Box>
    </Stack>
  );

  return (
    <>
      {/* Share template (off-screen) */}
      <ShareTemplate divRef={shareRef} cardTitle={card.title}>
        <Paper
          elevation={4}
          sx={{
            width: "100%",
            height: "auto",
            borderRadius: 6,
            overflow: "hidden",
          }}
        >
          {buildCardContent(true)}
        </Paper>
      </ShareTemplate>

      {/* Visible card */}
      <Paper
        elevation={3}
        sx={{
          // Square in the desktop 3-up grid, where it reads as a card wall. In the single-column
          // mobile layout a square is ~a full screen of mostly blank space per card, so there the
          // card is sized by its content instead.
          aspectRatio: { xs: "auto", md: "1" },
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          bgcolor: "background.paper",
        }}
      >
        {buildCardContent(false)}

        {/* Share button */}
        <IconButton
          size="small"
          onClick={handleShareClick}
          sx={{
            position: "absolute",
            bottom: 6,
            right: 6,
            bgcolor: "action.hover",
            "&:hover": { bgcolor: "action.selected" },
          }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Paper>

      {/* Share dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, p: 3, overflow: "hidden" }}>
          {rendering || !previewUrl ? (
            <CircularProgress />
          ) : (
            <Box component="img" src={previewUrl} sx={{ maxWidth: "100%", maxHeight: "65vh", objectFit: "contain", borderRadius: 2 }} />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1, pb: 2 }}>
          <Button variant="outlined" onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleDownload} disabled={!previewUrl}>
            Download
          </Button>
          {typeof navigator.share === "function" && (
            <Button variant="contained" color="secondary" onClick={handleShare} disabled={!previewUrl}>
              Share
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
